import { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Search } from "lucide-react";
import { images, menuCategories, menuItems } from "../data/siteData";
import { PageHero } from "../components/PageHero";
import { DishCard } from "../components/DishCard";
import { Reveal } from "../components/Reveal";

export default function Menu() {
  const [category, setCategory] = useState("All");
  const [query, setQuery] = useState("");

  useEffect(() => {
    document.title = "Menu | Mango – World Cuisine Restaurant, Bhavnagar";
  }, []);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    return menuItems.filter(
      (d) =>
        (category === "All" || d.category === category) &&
        (!q || d.name.toLowerCase().includes(q) || d.description.toLowerCase().includes(q))
    );
  }, [category, query]);

  const grouped = useMemo(() => {
    if (category !== "All") return null;
    return menuCategories
      .map((c) => ({ category: c, items: filtered.filter((d) => d.category === c) }))
      .filter((g) => g.items.length > 0);
  }, [category, filtered]);

  const Grid = ({ items }) => (
    <motion.div layout className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
      <AnimatePresence mode="popLayout">
        {items.map((dish) => (
          <motion.div
            layout
            key={dish.name}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.96 }}
            transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
          >
            <DishCard dish={dish} />
          </motion.div>
        ))}
      </AnimatePresence>
    </motion.div>
  );

  return (
    <>
      <PageHero
        overline="The Menu"
        lines={["Ten cuisines.", "One kitchen."]}
        image={images.heroMenu}
        description="From Burmese khau sue to bubbling fondues — every dish is vegetarian-friendly and made to be shared."
      />

      <div
        data-testid="menu-filter-bar"
        className="sticky top-20 z-40 bg-ivory/90 backdrop-blur-xl border-b border-line"
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-4 flex flex-col md:flex-row md:items-center gap-4">
          <div className="relative md:w-72 shrink-0">
            <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-charcoal/40" />
            <input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search the Menu"
              aria-label="Search the menu"
              data-testid="menu-search-input"
              className="w-full rounded-full border border-line bg-white py-2.5 pl-10 pr-4 text-sm text-charcoal placeholder:text-charcoal/40 focus:border-gold focus:outline-none transition-colors duration-300"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto no-scrollbar -mx-6 px-6 md:mx-0 md:px-0 md:flex-wrap">
            {["All", ...menuCategories].map((c) => (
              <button
                key={c}
                data-testid={`menu-filter-${c.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                onClick={() => setCategory(c)}
                aria-pressed={category === c}
                className={`shrink-0 rounded-full px-4 py-2 text-xs font-semibold tracking-wide transition-colors duration-300 ${
                  category === c
                    ? "bg-forest text-ivory"
                    : "bg-sand text-charcoal/70 hover:bg-forest/10 hover:text-forest"
                }`}
              >
                {c}
              </button>
            ))}
          </div>
        </div>
      </div>

      <section data-testid="menu-list" className="py-16 md:py-24 min-h-[50vh]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          {filtered.length === 0 ? (
            <Reveal className="py-20 text-center">
              <p className="font-display text-2xl text-charcoal/70">Nothing found for &ldquo;{query}&rdquo;</p>
              <p className="mt-2 text-sm text-charcoal/50">Try another dish or category.</p>
            </Reveal>
          ) : grouped ? (
            grouped.map((g, gi) => (
              <div key={g.category} className={gi > 0 ? "mt-16" : ""}>
                <Reveal>
                  <h2 className="font-display text-2xl md:text-3xl text-forest flex items-center gap-4">
                    {g.category}
                    <span className="h-px flex-1 bg-line" />
                    <span className="text-xs font-body font-semibold tracking-[0.2em] uppercase text-charcoal/40">
                      {g.items.length} dishes
                    </span>
                  </h2>
                </Reveal>
                <div className="mt-8">
                  <Grid items={g.items} />
                </div>
              </div>
            ))
          ) : (
            <Grid items={filtered} />
          )}
        </div>
      </section>
    </>
  );
}
